import { useTranslation } from 'react-i18next';

import type { ExpenseResponse, SalesSummaryResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { bucketValues, daysBetween, granularityFor, percent } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { compact, count, pounds } from '../../i18n/format';
import { magnitude } from '../catalog/money';

/** Past this many vendors the rest are behind "Show all". */
const FIRST_VENDORS = 8;

/**
 * The Expenses screen's charts (CR-36), from the expenses the screen already loads for its branch
 * and dates, and — for whoever may read the takings — the money summary for the same days (CR-34).
 *
 * What a manager asks of this screen is "what is costing us, and is it in proportion". So: what was
 * spent and what still waits for an approval (spent but unchecked, the one thing here somebody has
 * to act on); spending day by day; by category and by vendor, which is where a cost can actually be
 * negotiated; and spending against sales before VAT, since VAT is collected for the state and was
 * never the clinic's to spend.
 *
 * A rejected expense is not spending, by the rule the totals already follow, so it counts nowhere
 * here. One waiting for approval counts as spent, marked as such: the money has usually gone.
 */
export function ExpensesOverview({
  expenses,
  sales,
  from,
  to,
}: {
  readonly expenses: readonly ExpenseResponse[];
  /** The money summary for the same branch and days; absent for someone who may not read it. */
  readonly sales: SalesSummaryResponse | undefined;
  readonly from: string;
  readonly to: string;
}) {
  const { t, i18n } = useTranslation();

  const kept = expenses.filter((expense) => expense.status !== 'REJECTED');
  const spent = sum(kept.map((expense) => magnitude(expense.amount)));
  const waiting = kept.filter((expense) => expense.status === 'RECORDED');
  const waitingAmount = sum(waiting.map((expense) => magnitude(expense.amount)));
  const salesNet = sales ? magnitude(sales.soldNet) : null;

  const granularity = granularityFor(daysBetween(from, to));
  const byDay = new Map<string, number[]>();
  for (let day = from; day <= to; day = nextDay(day)) {
    byDay.set(day, [0, 0]);
  }
  for (const expense of kept) {
    const figures = byDay.get(expense.incurredOn ?? '');
    if (figures) {
      figures[0] = (figures[0] ?? 0) + magnitude(expense.amount);
      figures[1] = (figures[1] ?? 0) + 1;
    }
  }
  const columns: Column[] = bucketValues(
    [...byDay.entries()].map(([date, values]) => ({ date, values })),
    granularity,
  ).map((bucket) => {
    const [amount = 0, entries = 0] = bucket.values;
    return {
      key: bucket.start,
      label: bucketLabel(bucket, granularity, i18n.language),
      description: t('overview.expenses.columnDescription', {
        when: bucketTitle(bucket, granularity, i18n.language, (first, last) =>
          t('overview.weekOf', { from: first, to: last }),
        ),
        amount: pounds(amount),
        count: entries,
      }),
      segments: [{ value: amount, colour: 'var(--chart-1)' }],
    };
  });

  const categories = group(kept, (expense) => expense.category ?? 'OTHER', (key) => t(`expenses.categories.${key}`));
  const vendors = group(
    kept,
    (expense) => expense.vendor?.trim() || '',
    (key) => key || t('overview.expenses.noVendor'),
  );
  const against: RankedBar[] =
    salesNet === null
      ? []
      : [
          { key: 'sales', label: t('overview.expenses.salesNet'), value: salesNet },
          { key: 'spent', label: t('overview.expenses.spentRow'), value: spent, tone: spent > salesNet ? 'alert' : undefined },
        ];

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.expenses.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.expenses.spent', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(spent))}</span>
          <span className="measure__note">{t('overview.expenses.entries', { count: kept.length })}</span>
        </div>
        <div className={waiting.length > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.expenses.waiting', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(waitingAmount))}</span>
          <span className="measure__note">{t('overview.expenses.toApprove', { count: waiting.length })}</span>
        </div>
        {salesNet !== null ? (
          <>
            <div className="measure">
              <span className="measure__label">{t('overview.expenses.sales', { currency: 'EGP' })}</span>
              <span className="measure__value">{count(Math.round(salesNet))}</span>
              <span className="measure__note">
                {t('overview.expenses.spentShare', { share: percent(spent, salesNet) })}
              </span>
            </div>
            <div className={spent > salesNet ? 'measure measure--alert' : 'measure'}>
              <span className="measure__label">{t('overview.expenses.left', { currency: 'EGP' })}</span>
              <span className="measure__value">{count(Math.round(salesNet - spent))}</span>
              <span className="measure__note">{t('overview.expenses.leftNote')}</span>
            </div>
          </>
        ) : null}
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.expenses.overTime')}</h2>
            <p className="muted">{t(`overview.per.${granularity}`)}</p>
          </div>
          <Columns columns={columns} label={t('overview.expenses.overTime')} formatTick={compact} />
        </section>

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.expenses.byCategory')}</h2>
            <p className="muted">{t('overview.expenses.byCategoryNote')}</p>
          </div>
          {categories.length > 0 ? (
            <RankedBars rows={categories} format={pounds} />
          ) : (
            <p className="muted">{t('expenses.none')}</p>
          )}
        </section>

        {vendors.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.expenses.byVendor')}</h2>
              <p className="muted">{t('overview.expenses.byVendorNote')}</p>
            </div>
            <RankedBars rows={vendors} format={pounds} firstRows={FIRST_VENDORS} />
          </section>
        ) : null}

        {against.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.expenses.against')}</h2>
              <p className="muted">{t('overview.expenses.againstNote')}</p>
            </div>
            <RankedBars rows={against} format={pounds} share={false} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

function sum(values: readonly number[]): number {
  return values.reduce((running, value) => running + value, 0);
}

function nextDay(isoDate: string): string {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}

/** Amounts added up per key, largest first. */
function group(
  expenses: readonly ExpenseResponse[],
  keyOf: (expense: ExpenseResponse) => string,
  label: (key: string) => string,
): RankedBar[] {
  const totals = new Map<string, number>();
  for (const expense of expenses) {
    const key = keyOf(expense);
    totals.set(key, (totals.get(key) ?? 0) + magnitude(expense.amount));
  }
  return [...totals.entries()]
    .map(([key, value]) => ({ key: key || 'none', label: label(key), value }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}
